import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { finalize, interval, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { NGXLogger } from 'ngx-logger';
import { State } from 'src/app/dashboards/state';
import { Store } from '@ngrx/store';
import { realTimeService } from 'src/app/services/services/realtime.service';

type WindowRange = 'day' | 'week';

export interface TickerServiceRow {
  accessPoint: string;           // e.g. service number
  description: string;

  totalReceived: number;
  totalAnswered: number;

  answerPercent: number | null;  // 0..1 or null
  formattedAnswerPercent: string;
}

export interface TickerServiceTotals {
  statisticsTitle: string;       // e.g. "Totals"
  formattedTotalReceived: string;
  formattedTotalAnswered: string;
  formattedAnswerPercent: string;
}

@Component({
    selector: 'app-ticker-services',
    templateUrl: './ticker-services.component.html',
    styleUrls: ['./ticker-services.component.scss'],
    standalone: false
})
export class TickerServicesComponent implements OnInit, OnDestroy {
  // Flags / config
  pollingIntervalMs = 10000; // old: Model.AjaxRefreshInterval
  selectedWindow: WindowRange = 'day';

  // Table
  displayedColumns: string[] = [
    'accessPoint',
    'description',
    'totalReceived',
    'totalAnswered',
    'answerPercent'
  ];
  displayedFooterColumns = this.displayedColumns;
  BreadcrumbsLink = [
    {"label": "Home", "url": "/"}, 
    {"label": "Real-Time", "url": "/real-time/overview"}, 
    {"label": "Ticker Services", "url": "/real-time/ticker-services"}];

  dataSource = new MatTableDataSource<TickerServiceRow>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  // Footer totals (from API or computed)
  totals: TickerServiceTotals | null = null;

  // UI state
  filter = '';
  isLoading = true;

  // Polling
  private pollingSub?: Subscription;


    constructor(
      private dialog: MatDialog,
      private realTimeService: realTimeService,
      private logger: NGXLogger,
      private store: Store<State>,
      private i18nextPipe: I18NextPipe,
    ) { }



  ngOnInit(): void {
    // Sorting accessor to sort by numeric values, not formatted strings
    this.dataSource.sortingDataAccessor = (row: TickerServiceRow, column: string) => {
      switch (column) {
        case 'accessPoint': return (row.accessPoint || '').toLowerCase();
        case 'description': return (row.description || '').toLowerCase();
        case 'totalReceived': return row.totalReceived ?? -1;
        case 'totalAnswered': return row.totalAnswered ?? -1;
        case 'answerPercent': return row.answerPercent == null ? -1 : Math.round(row.answerPercent * 100);
        default: return '';
      }
    };
    this.dataSource.sort = this.sort;

    // Filter (same UX as other pages)
    this.dataSource.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.accessPoint,
        row.description,
        String(row.totalReceived),
        String(row.totalAnswered),
        row.formattedAnswerPercent
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    this.loadData(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // --- Data load (poll) ---
  private loadData(showLoader = false): void {
    if (showLoader) this.isLoading = true;

    this.realTimeService.getTickerServices(this.selectedWindow === 'day' ? 0 : 1)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(res => {
        this.dataSource.data = res.rows ?? [];
        this.totals = res.totals ?? null;
      });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      this.loadData(false);
    });
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  // --- Filter handlers ---
  applyFilterFromProperty() {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  // --- Window toggle ---
  onSelectWindow(range: WindowRange) {
    if (this.selectedWindow === range) return;
    this.selectedWindow = range;
    this.loadData(true);
  }

  // --- Help ---
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18nextPipe.transform('ticker:help_title_services'),
        text: this.i18nextPipe.transform('ticker:help_services'),
        renderAsHtml: false
      }
    });
  }

  // --- Totals (client-side for mock; use API totals if you have them) ---
  private computeTotals(rows: TickerServiceRow[]): TickerServiceTotals {
    const totalReceived = rows.reduce((s, r) => s + (r.totalReceived || 0), 0);
    const totalAnswered = rows.reduce((s, r) => s + (r.totalAnswered || 0), 0);
    const ansPct = totalReceived ? totalAnswered / totalReceived : null;

    return {
      statisticsTitle: this.i18nextPipe.transform('ticker:statistics_title', { defaultValue: 'Totals' }),
      formattedTotalReceived: String(totalReceived),
      formattedTotalAnswered: String(totalAnswered),
      formattedAnswerPercent: ansPct == null ? '—' : `${Math.round(ansPct * 100)} %`,
    };
  }


}
